"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.REGISTER_HEADERS = exports.CASH_HEADERS = exports.REGISTER_WIDTHS = exports.CASH_WIDTHS = void 0;
exports.toAmount = toAmount;
exports.toNumber = toNumber;
exports.formatAmount = formatAmount;
exports.previewExpense = previewExpense;
exports.previewInflow = previewInflow;
exports.previewPayer = previewPayer;
exports.previewNet = previewNet;
exports.previewCashRow = previewCashRow;
exports.previewRegisterBlock = previewRegisterBlock;
const pettyCashApi_1 = require("./pettyCashApi");
const fuelNote_1 = require("./fuelNote");
const trackingNumber_1 = require("./trackingNumber");
/**
 * Kwota z pola tekstowego. W arkuszu i na klawiaturze telefonu używa się przecinka.
 * `toAmount` zwraca NaN dla nie-liczby (walidacja musi je odróżnić), `toNumber` zero
 * (podgląd i sumy wolą policzyć się z tym, co jest).
 */
function toAmount(value) {
    return Number.parseFloat(String(value ?? "").replace(",", "."));
}
function toNumber(value) {
    const parsed = toAmount(value);
    return Number.isFinite(parsed) ? parsed : 0;
}
/** Kwoty w arkuszu mają dwa miejsca po przecinku i przecinek jako separator. */
function formatAmount(value) {
    return value.toFixed(2).replace(".", ",");
}
/** Kolumna wydatku to w arkuszu formuła `=E+F`. */
function previewExpense(input) {
    if (input.entryKind === "ADVANCE")
        return 0;
    if (input.entryKind === "NO_DOCUMENT")
        return toNumber(input.noDocumentAmount);
    return toNumber(input.grossAmount);
}
/** Kolumna wpływu: karta lustrzanie do wydatku, wypłata zaliczki wprost, gotówka pusto. */
function previewInflow(input) {
    if (input.settlementMethod === "CARD")
        return previewExpense(input);
    if (input.settlementMethod === "ADVANCE")
        return toNumber(input.inflowAmount);
    return null;
}
/**
 * Kolumna „kto zapłacił” łączy sposób płatności z osobą: `got. Karolina`, `karta Krzysiek`.
 * Przedrostek wpisany przez człowieka zdejmujemy, żeby nie powstało `got. got. Michał`.
 */
function previewPayer(input) {
    const name = input.payerLabel.replace(/^\s*(got\.?|gotówk[aą]|karta\.?|kart[ąa])\s+/i, "").trim();
    if (!name)
        return "";
    return input.settlementMethod === "CARD" ? `karta ${name}` : `got. ${name}`;
}
/** Netto przy poczcie równa się brutto — usługa jest zwolniona z VAT. */
function previewNet(input) {
    if (input.entryKind === "POSTAL")
        return input.grossAmount;
    if (input.entryKind === "NO_DOCUMENT" || input.entryKind === "ADVANCE")
        return "";
    return input.netAmount || input.grossAmount;
}
const editable = (value, field, numeric = false, multiline = false) => ({ value, field, numeric, multiline });
const derived = (value, hint, numeric = false, multiline = false) => ({ value, hint, numeric, multiline });
/** Wiersz arkusza zaliczek, kolumny A..J, w kolejności arkusza. */
function previewCashRow(input) {
    const inflow = previewInflow(input);
    const hasDocumentAmounts = pettyCashApi_1.KINDS_WITH_DOCUMENT.includes(input.entryKind);
    const isPostal = input.entryKind === "POSTAL";
    const isAdvance = input.entryKind === "ADVANCE";
    const isNoDocument = input.entryKind === "NO_DOCUMENT";
    return [
        editable(input.entryDate, "entryDate"),
        isAdvance
            ? editable(input.inflowAmount, "inflowAmount", true)
            : derived(inflow === null ? "" : formatAmount(inflow), input.settlementMethod === "CARD"
                ? "Przy karcie arkusz wpisuje tu formułę równą wydatkowi — saldo portfela się nie zmienia."
                : "Przy gotówce ta kolumna zostaje pusta.", true),
        editable(input.description, "description", false, true),
        isPostal
            ? derived(previewNet(input).replace(".", ","), "Usługi pocztowe są zwolnione z VAT, więc netto równa się kwocie faktury.", true)
            : hasDocumentAmounts
                ? editable(input.netAmount, "netAmount", true)
                : derived("", "Ten rodzaj wpisu nie ma kwoty netto.", true),
        hasDocumentAmounts
            ? editable(input.grossAmount, "grossAmount", true)
            : derived("", "Ten rodzaj wpisu nie ma kwoty brutto.", true),
        isNoDocument
            ? editable(input.noDocumentAmount, "noDocumentAmount", true)
            : derived("", "Ta kolumna dotyczy tylko wydatków bez dokumentu.", true),
        derived(formatAmount(previewExpense(input)), "Arkusz liczy tę kolumnę formułą: brutto plus kwota bez dokumentu.", true),
        hasDocumentAmounts
            ? editable(input.documentNumber, "documentNumber")
            : derived("", "Ten rodzaj wpisu nie ma numeru dokumentu."),
        derived(previewPayer(input), "Składa się z pól „czym zapłacono” i „kto zapłacił”."),
        input.entryKind === "FUEL" && input.odometerReading.trim()
            ? derived((0, fuelNote_1.fuelNote)(input.odometerReading, input.note), "Składa się z pól „stan licznika” i „uwaga”.", false, true)
            : editable(input.note, "note", false, true),
    ];
}
/**
 * Szerokości kolumn podglądu. Numer nadania musi zmieścić 22 znaki, bo ucięta ostatnia
 * cyfra wygląda jak poprawny numer. Data i kwota dostają mniej, bo więcej nie potrzebują.
 */
exports.CASH_WIDTHS = [96, 104, 230, 76, 76, 92, 88, 176, 132, 170];
exports.REGISTER_WIDTHS = [44, 150, 176, 260, 190, 96, 78, 132];
exports.CASH_HEADERS = [
    "Data",
    "ZALICZKA, zapłata kartą (wpływ)",
    "OPIS",
    "NETTO",
    "BRUTTO",
    "BEZ FV / PARAGON",
    "wydatek",
    "saldo / Nr faktury",
    "kto zapłacił",
    "uwaga",
];
exports.REGISTER_HEADERS = [
    "nr",
    "nr faktury / lp",
    "adresat",
    "co wysłano",
    "nr listu",
    "data",
    "kwota",
    "kto zapłacił",
];
/** Blok rejestru listów: nagłówek, pozycje, suma — dokładnie jak w arkuszu. */
function previewRegisterBlock(input, items) {
    const total = items.reduce((sum, item) => sum + toNumber(item.amount), 0);
    const header = [
        derived("?", "Numer bloku nadaje arkusz: o jeden wyższy od ostatniego."),
        editable(input.documentNumber, "documentNumber"),
        derived("", "Numer faktury zajmuje w arkuszu trzy scalone komórki."),
        derived("", "Numer faktury zajmuje w arkuszu trzy scalone komórki."),
        derived("nr listu", "Etykieta kolumny, przepisywana z poprzedniego bloku."),
        derived("data", "Etykieta kolumny, przepisywana z poprzedniego bloku."),
        derived("kwota", "Etykieta kolumny, przepisywana z poprzedniego bloku."),
        derived("", ""),
    ];
    const rows = items.map((item, index) => [
        derived("", "Numer bloku stoi tylko w nagłówku — niżej kryje go scalenie."),
        derived(String(index + 1), "Numer porządkowy listu w bloku."),
        {
            value: item.addressee,
            item: { index, field: "addressee" },
        },
        {
            value: item.contentsDescription,
            item: { index, field: "contentsDescription" },
            multiline: true,
        },
        {
            value: item.trackingNumber ? (0, trackingNumber_1.formatTrackingNumber)(item.trackingNumber) : "",
            item: { index, field: "trackingNumber" },
        },
        index === 0
            ? editable(input.entryDate, "entryDate")
            : derived("", "Data stoi tylko przy pierwszym liście — resztę kryje scalenie."),
        { value: item.amount, item: { index, field: "amount" }, numeric: true },
        derived("", ""),
    ]);
    const sum = [
        derived("", ""),
        derived("", ""),
        derived("", ""),
        derived("", ""),
        derived("", ""),
        derived("", ""),
        derived(formatAmount(total), "Arkusz liczy sumę formułą z kwot listów.", true),
        derived(previewPayer(input), "Składa się z pól „czym zapłacono” i „kto zapłacił”."),
    ];
    return [header, ...rows, sum];
}
